import React, { useCallback, useMemo, useState } from 'react';
import {
  AppButton,
  Box,
  CardTypesEnum,
  CrossIcon,
  Inscription,
  LinkButton,
  List,
  OverflowText,
  useFormContext,
  WarningIcon,
  WarnModal,
} from '@common-fe/common-fe';

import PlanTypeIcon from '@/modules/plan/PlanSetup/Sidebar/PlanTypeIcon';
import { SERVICE_TYPE_LABELS } from '@/modules/ProcessingDefinition/Services/Services.constants';
import colors from '@/styles/colors';
import theme from '@/styles/theme';

import {
  PayoutDefinitionFormValues,
  PayoutOrderPlan,
  PayoutOrderService,
} from '../../../PayoutDefinitionSetup.types';
import { getServiceColumnErrorKey } from '../hooks/usePayoutOrderValidation';
import { AnimatedCategoryName, AnimatedColumn, RemoveServiceButton } from '../PayoutOrder.styles';

import { COLUMN_WIDTH_MAP, SERVICE_NAME_WIDTH_MAP } from './PayoutOrder.constants';
import PayoutOrderPlanDropdown from './PayoutOrderPlanDropdown';

const PAGE_SIZE = 50;

interface UpdateServicePlanParams {
  categoryType: string;
  rowId: string;
  order: number;
  plan: PayoutOrderPlan | null;
}

interface PayoutOrderServicesListProps {
  services: PayoutOrderService[];
  isEditMode?: boolean;
  onRemoveService?: (rowId: string) => void;
  columns: number[];
  categoryType: string;
  onUpdateServicePlan: (params: UpdateServicePlanParams) => void;
  columnsCount: number;
}

/** Memoized individual service row to avoid re-rendering all rows on column change */
interface ServiceRowProps {
  service: PayoutOrderService;
  index: number;
  columns: number[];
  columnsCount: number;
  isEditMode?: boolean;
  onPlanChange: (rowId: string, order: number, plan: PayoutOrderPlan | null) => void;
  onRequestRemove: (service: PayoutOrderService) => void;
}

const ServiceRow: React.FC<ServiceRowProps> = ({
  service,
  index,
  columns,
  columnsCount,
  isEditMode,
  onPlanChange,
  onRequestRemove,
}) => {
  const { formState } = useFormContext<PayoutDefinitionFormValues>();

  const handleRemoveClick = useCallback(() => onRequestRemove(service), [onRequestRemove, service]);

  return (
    <List.Item
      key={service.rowId}
      data-testid={`payout-order_service_${service.rowId}`}
      height="56px"
      margin={index > 0 ? { top: 'spacing4' } : undefined}
      pad={{ horizontal: 'spacing12' }}
      disableHover
    >
      <Box direction="row" align="center" style={{ flex: 1, position: 'relative' }}>
        <Box direction="row" align="center" style={{ flex: 1 }} gap="s">
          <AnimatedCategoryName
            direction="column"
            justify="center"
            style={{ width: SERVICE_NAME_WIDTH_MAP[columnsCount], overflow: 'hidden' }}
          >
            <OverflowText
              title={service.name}
              textStyle={{ fontWeight: '400' }}
              data-testid={`payout-order_service_${service.rowId}_name`}
            />
            {service.serviceType && (
              <Inscription size="12px" color="textSecondary" lineHeight="18px" weight={500}>
                {SERVICE_TYPE_LABELS[service.serviceType as keyof typeof SERVICE_TYPE_LABELS] ||
                  service.serviceType}
              </Inscription>
            )}
          </AnimatedCategoryName>

          {columns.map((columnIndex) => {
            const planForColumn = service.plans.find((p) => p.order === columnIndex);
            const columnErrorKey = getServiceColumnErrorKey(service.rowId, columnIndex);
            const columnError = (formState.errors as Record<string, { message?: string }>)?.[columnErrorKey]
              ?.message;
            return (
              <AnimatedColumn key={columnIndex} style={{ width: COLUMN_WIDTH_MAP[columnsCount] }}>
                {isEditMode ? (
                  <PayoutOrderPlanDropdown
                    testId={`payout-order_service_${service.rowId}_column_${columnIndex}`}
                    value={planForColumn || null}
                    onChange={(plan) => onPlanChange(service.rowId, columnIndex, plan)}
                    columnsCount={columnsCount}
                    errorMessage={columnError}
                  />
                ) : (
                  planForColumn && (
                    <Box direction="row" align="center" gap="spacing8">
                      {planForColumn.accountType && (
                        <PlanTypeIcon
                          type={planForColumn.accountType as CardTypesEnum}
                          size="20px"
                          color={colors.cardTypes[planForColumn.accountType as keyof typeof colors.cardTypes]}
                        />
                      )}
                      <OverflowText title={planForColumn.name} textStyle={{ fontWeight: '400' }} />
                    </Box>
                  )
                )}
              </AnimatedColumn>
            );
          })}
        </Box>

        {isEditMode ? (
          <Box style={{ flexShrink: 0, position: 'relative', zIndex: 1 }} align="center" width="20px">
            <RemoveServiceButton
              data-testid={`payout-order_service_${service.rowId}_remove-button`}
              onClick={handleRemoveClick}
            >
              <CrossIcon size="10px" color="iconPrimary" />
            </RemoveServiceButton>
          </Box>
        ) : (
          <Box
            style={{ flexShrink: 0, position: 'relative', zIndex: 1 }}
            align="center"
            width="40px"
            margin={{ left: 'spacing12' }}
          />
        )}
      </Box>
    </List.Item>
  );
};

const MemoizedServiceRow = React.memo(ServiceRow);

const PayoutOrderServicesList: React.FC<PayoutOrderServicesListProps> = ({
  services,
  isEditMode,
  onRemoveService,
  columns,
  categoryType,
  onUpdateServicePlan,
  columnsCount,
}) => {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [serviceToRemove, setServiceToRemove] = useState<PayoutOrderService | null>(null);

  const visibleServices = useMemo(() => services.slice(0, visibleCount), [services, visibleCount]);
  const hasMoreThanOnePage = services.length > PAGE_SIZE;
  const remainingCount = services.length - visibleCount;
  const isAllVisible = visibleCount >= services.length;

  const handleShowMore = useCallback(() => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
  }, []);

  const handleConfirmRemove = useCallback(() => {
    if (serviceToRemove && onRemoveService) {
      onRemoveService(serviceToRemove.rowId);
    }
    setServiceToRemove(null);
  }, [serviceToRemove, onRemoveService]);

  const handlePlanChange = useCallback(
    (rowId: string, order: number, plan: PayoutOrderPlan | null) => {
      const planWithOrder = plan ? { ...plan, order } : null;
      onUpdateServicePlan({
        categoryType,
        rowId,
        order,
        plan: planWithOrder,
      });
    },
    [categoryType, onUpdateServicePlan]
  );

  const handleRequestRemove = useCallback((service: PayoutOrderService) => setServiceToRemove(service), []);

  return (
    <Box>
      <Box style={{ position: 'relative' }}>
        <List>
          {visibleServices.map((service, index) => (
            <MemoizedServiceRow
              key={service.rowId}
              service={service}
              index={index}
              columns={columns}
              columnsCount={columnsCount}
              isEditMode={isEditMode}
              onPlanChange={handlePlanChange}
              onRequestRemove={handleRequestRemove}
            />
          ))}
        </List>
        {!isAllVisible && (
          <Box
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '60px',
              background: theme.colors.fade1,
            }}
          />
        )}
      </Box>

      {hasMoreThanOnePage && (
        <Box align="center" pad={{ vertical: 'spacing16' }}>
          {isAllVisible ? (
            <Inscription size="14px" color="textSecondary" lineHeight="20px">
              End of the list
            </Inscription>
          ) : (
            <LinkButton onClick={handleShowMore} linkInlineMode>
              Show More ({remainingCount})
            </LinkButton>
          )}
        </Box>
      )}

      <WarnModal
        testId="payout-order_service_remove-modal"
        iconNode={<WarningIcon size="48px" />}
        visible={!!serviceToRemove}
        header="Remove Service?"
        onSetVisible={(visible) => {
          if (!visible) setServiceToRemove(null);
        }}
        cancelButtonNode={
          <Box direction="row" gap="spacing12">
            <AppButton
              testId="payout-order_service_remove-cancel"
              buttonType="secondary"
              onClick={() => setServiceToRemove(null)}
              width="150px"
            >
              Cancel
            </AppButton>
            <AppButton
              testId="payout-order_service_remove-confirm"
              color="red"
              onClick={handleConfirmRemove}
              width="150px"
            >
              Yes, Remove
            </AppButton>
          </Box>
        }
      />
    </Box>
  );
};

export default React.memo(PayoutOrderServicesList);
